import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    const s = iso.endsWith("Z") || iso.match(/[+-]\d{2}:\d{2}$/) ? iso : iso + "Z";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 19).replace("T", " ");
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    });
  } catch { return String(iso).slice(0, 19); }
};

const roleColor = (r) => ({
  admin: "bg-purple-50 text-purple-700 border-purple-200",
  ra: "bg-blue-50 text-blue-700 border-blue-200",
  crm: "bg-amber-50 text-amber-700 border-amber-200",
  customer: "bg-emerald-50 text-emerald-700 border-emerald-200",
}[r] || "bg-zinc-50 text-zinc-700 border-zinc-200");

export default function LoginHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ email: "", role: "" });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.email) params.email = filters.email;
      if (filters.role) params.role = filters.role;
      const r = await api.get("/admin/login-history", { params });
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(`Failed to load login history: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="login-history-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Admin</div>
        <h1 className="font-display font-bold text-3xl text-zinc-950 mt-1">Login History</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Timestamps of successful sign-ins across all roles. Newest first.
        </p>
      </div>

      <div className="flex gap-2 items-end flex-wrap">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Email contains</label>
          <Input
            value={filters.email}
            onChange={(e) => setFilters({ ...filters, email: e.target.value })}
            placeholder="e.g. vivek@"
            className="w-64 mt-1"
            data-testid="filter-email"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Role</label>
          <select
            value={filters.role}
            onChange={(e) => setFilters({ ...filters, role: e.target.value })}
            className="block mt-1 border border-zinc-200 rounded-md px-3 py-2 text-sm w-32"
            data-testid="filter-role"
          >
            <option value="">All</option>
            <option value="admin">admin</option>
            <option value="ra">ra</option>
            <option value="crm">crm</option>
            <option value="customer">customer</option>
          </select>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">When (IST)</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Method</th>
                <th className="px-4 py-3 text-left">IP</th>
                <th className="px-4 py-3 text-left">User Agent</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-400" data-testid="empty-state">No login records yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.login_id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`login-row-${r.login_id}`}>
                  <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{fmtIST(r.timestamp)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{r.name || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.email}</td>
                  <td className="px-4 py-2"><Badge className={roleColor(r.role)}>{r.role}</Badge></td>
                  <td className="px-4 py-2 text-xs">{r.auth_method || "password"}</td>
                  <td className="px-4 py-2 font-mono text-xs text-zinc-500">{r.ip || "—"}</td>
                  <td className="px-4 py-2 text-xs text-zinc-500 max-w-[240px]"><div className="truncate" title={r.user_agent}>{r.user_agent || "—"}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
